import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import type { SEOInfoServerType } from '@/services/seo-info/seoInfo';
import type { DocsIdTypeData } from '@/shared/constant/DocsIdTypeData';
import type { ItemPathPrefix } from '@/shared/constant/ItemPathPrefix';
import { OnlineType } from '@/shared/constant/OnlineType';
import { buildResourcePath } from '@/shared/constant/resource-path/BuildResourcePathByConfig';
import type { DocsSitePublishedUrlRules } from '@/shared/constant/resource-path/ResourcePathDefs';
import { TypeInRoute } from '@/shared/constant/TypeInRoute';
// import { navigateToResourceDetailPage } from '@/utils/navigation';
import { useNavigate, useSearchParams } from '@remix-run/react';
import type { VersionSetting } from '@tarslib/types/lib/publishedProject';
import toInteger from 'lodash-es/toInteger';
import type React from 'react';

export type SearchHitProps = {
  hit: {
    objectID: string;
    type: string;
    title: string;
    subtitle: string;
    description: string;
    tags: string[];
    content: string[];
  };
  children: React.ReactNode;
};

export const enum ReIndexEntityType {
  ENDPOINT = 'ENDPOINT',
  DOC = 'DOC',
  API_FOLDER = 'API_FOLDER',
  WEB_SOCKET_API = 'WEB_SOCKET_API',
  CUSTOM_ENDPOINT = 'CUSTOM_ENDPOINT',
  SOCKET_IO_API = 'SOCKET_IO_API',
  DATA_SCHEMA = 'DATA_SCHEMA',
}

const convertReIndexEntityTypeToTypeInRoute = (reIndexEntityType: ReIndexEntityType | string) => {
  return (
    {
      [ReIndexEntityType.ENDPOINT]: TypeInRoute.Api,
      [ReIndexEntityType.DOC]: TypeInRoute.Doc,
      [ReIndexEntityType.API_FOLDER]: TypeInRoute.ApiFolder,
      [ReIndexEntityType.WEB_SOCKET_API]: TypeInRoute.WebSocketApi,
      [ReIndexEntityType.CUSTOM_ENDPOINT]: TypeInRoute.CustomEndpoint,
      [ReIndexEntityType.SOCKET_IO_API]: TypeInRoute.SocketIOApi,
      [ReIndexEntityType.DATA_SCHEMA]: TypeInRoute.ApiSchema,
    }[reIndexEntityType] || TypeInRoute.Doc
  );
};

export const handleClickSearchHit = (
  objectID: string,
  context: {
    navigate: ReturnType<typeof useNavigate>;
    urlSearchParameters: URLSearchParams;
    docsIdTypeData: DocsIdTypeData;
    versionSettings?: VersionSetting[];
    seoInfos?: SEOInfoServerType;
    urlRules?: DocsSitePublishedUrlRules;
    itemPathPrefix: ItemPathPrefix;
  },
) => {
  const {
    navigate,
    urlSearchParameters,
    docsIdTypeData,
    versionSettings,
    seoInfos,
    urlRules,
    itemPathPrefix,
  } = context;
  const urlSearchParametersObject = Object.fromEntries(urlSearchParameters);
  const [reIndexEntityType = '', itemId, projectBranchId] = objectID.split('-');
  const typeInRoute = convertReIndexEntityTypeToTypeInRoute(reIndexEntityType);
  const branchSlug = versionSettings?.find(
    (item) =>
      String(item.branchId) === projectBranchId && !item.isBranchDeleted && !item.isDefaultVersion,
  )?.slug;
  const url = buildResourcePath(
    {
      docsIdTypeData,
      itemPathPrefix: {
        ...itemPathPrefix,
        versionSlug: branchSlug ?? '',
      },
      seoInfos: seoInfos ?? new Map(),
      docsSiteSummary: {
        summary: {
          projectSetting: {
            advancedSettings: {
              publishedDocUrlRules: urlRules,
            },
          },
        },
      },
    },
    {
      id: toInteger(itemId),
      type: typeInRoute,
      name: '',
    },
    undefined,
    {
      urlSearchParametersObject,
    },
  );
  navigate(url);
};

const SearchHit: React.FC<SearchHitProps> = (props) => {
  const { hit, children } = props;

  const [urlSearchParameters] = useSearchParams();

  const branchId = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return undefined;
    }
    return x.docsIdTypeData.branchId;
  });

  const docsIdTypeData = useDocsDataModelStore((x) => x.docsIdTypeData);
  const urlRules = useDocsDataModelStore(
    (x) => x.projectSetting?.advancedSettings?.publishedDocUrlRules,
  );
  const versionSettings = useDocsDataModelStore((x) => x.versionSettings);
  const seoInfos = useDocsDataModelStore((x) => x.seoInfos);
  const itemPathPrefix = useDocsDataModelStore((x) => x.itemPathPrefix);
  const navigate = useNavigate();

  return (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a
      key={hit.objectID}
      title={hit.title}
      href="javascript:void(0)"
      onClick={() => {
        handleClickSearchHit(hit.objectID, {
          navigate,
          urlSearchParameters,
          docsIdTypeData,
          versionSettings,
          seoInfos,
          urlRules,
          itemPathPrefix: itemPathPrefix ?? {
            subdirectory: '',
            versionSlug: '',
            baseUrl: '',
            isSharedDocIndependentDomain: false,
          },
        });
      }}
    >
      {children}
    </a>
  );
};

export default SearchHit;
